export type PartyTypeValue = 'individual' | 'organization';
export type PartyApiTypeValue = PartyTypeValue | 'Individual' | 'Organization' | 0 | 1;
export type PartyIsActiveFilter = 'all' | 'active' | 'inactive';

export interface PartySearchItem {
  id: number;
  partyType: PartyApiTypeValue;
  isActive: boolean;
  displayName: string;
  taxId?: string | null;
  email?: string | null;
  phone?: string | null;
  city?: string | null;
  street?: string | null;
}

export interface PartyDto extends PartySearchItem {
  postalCode?: string | null;
  countryCode?: string | null;
  organizationName?: string | null;
  regon?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  addressSummary?: string | null;
}

export interface SearchPartiesResponseDto {
  items: PartySearchItem[];
  total: number;
  page: number;
  pageSize: number;
}

export interface PartySearchQueryArgs {
  search?: string;
  page?: number;
  pageSize?: number;
  isActive?: PartyIsActiveFilter;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

export interface PartyFormValues {
  partyType: PartyTypeValue;
  organizationName: string;
  taxId: string;
  regon: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  street: string;
  city: string;
  postalCode: string;
  countryCode: string;
}

export interface UpsertPartyRequest {
  partyType: 0 | 1;
  organizationName: string | null;
  taxId: string | null;
  regon: string | null;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  street: string | null;
  city: string | null;
  postalCode: string | null;
  countryCode: string | null;
}

export interface PartiesErrorFeedback {
  errorCode: string | null;
  message: string;
}

const normalizeNullableString = (value: string | null | undefined): string | null => {
  if (!value) {
    return null;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

const normalizeTaxId = (value: string | null | undefined): string | null => {
  const normalized = normalizeNullableString(value);
  if (!normalized) {
    return null;
  }

  const withoutSeparators = normalized.replace(/[\s\-./\\]/g, '');
  return withoutSeparators.length > 0 ? withoutSeparators : null;
};

const normalizeCountryCode = (value: string | null | undefined): string | null => {
  const normalized = normalizeNullableString(value);
  return normalized ? normalized.toUpperCase() : null;
};

export const normalizePartyType = (value: PartyApiTypeValue): PartyTypeValue =>
  value === 1 || value === 'Organization' || value === 'organization' ? 'organization' : 'individual';

export const mapPartyTypeToApiValue = (partyType: PartyTypeValue): 0 | 1 =>
  partyType === 'organization' ? 1 : 0;

export const mapPartyDtoToFormValues = (dto: PartyDto): PartyFormValues => ({
  partyType: normalizePartyType(dto.partyType),
  organizationName: dto.organizationName ?? '',
  taxId: dto.taxId ?? '',
  regon: dto.regon ?? '',
  firstName: dto.firstName ?? '',
  lastName: dto.lastName ?? '',
  email: dto.email ?? '',
  phone: dto.phone ?? '',
  street: dto.street ?? '',
  city: dto.city ?? '',
  postalCode: dto.postalCode ?? '',
  countryCode: dto.countryCode ?? '',
});

export const mapPartyFormValuesToRequest = (values: PartyFormValues): UpsertPartyRequest => {
  const isOrganization = values.partyType === 'organization';

  return {
    partyType: mapPartyTypeToApiValue(values.partyType),
    organizationName: isOrganization ? normalizeNullableString(values.organizationName) : null,
    taxId: isOrganization ? normalizeTaxId(values.taxId) : null,
    regon: isOrganization ? normalizeNullableString(values.regon) : null,
    firstName: !isOrganization ? normalizeNullableString(values.firstName) : null,
    lastName: !isOrganization ? normalizeNullableString(values.lastName) : null,
    email: normalizeNullableString(values.email),
    phone: normalizeNullableString(values.phone),
    street: normalizeNullableString(values.street),
    city: normalizeNullableString(values.city),
    postalCode: normalizeNullableString(values.postalCode),
    countryCode: normalizeCountryCode(values.countryCode),
  };
};
